import React, { useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import MainLayout from '../../../layouts/MainLayout'
import '../../../assets/styles/custom-styles.css'
import {
  faEdit,
  faTrash,
  faEye,
  faMapMarkerAlt,
  faUndo,
} from '@fortawesome/free-solid-svg-icons'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import { usePermissions } from '../../../hooks/usePermissions'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import ListHeader from '../../../components/ListHeader'
import useAction from '../../../hooks/useAction'
import { maskCep, maskCpfCnpj, maskPhone } from '../../../utils/maskUtils'
import DetailsSectionRenderer from '../../../components/DetailsSectionRenderer'
import { detailsCustomerFields } from '../../../constants/forms/customerFields'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useUrlFilters from '../../../hooks/useUrlFilters'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { Box } from '@mui/material'
import { safe } from '../../../utils/objectUtils'

const CustomerDetailsPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const location = useLocation()
  const { canAccess } = usePermissions()
  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()
  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate)
  const queryClient = useQueryClient()

  // --- Gerenciamento de Modal de Confirmação com useAction ---
  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate)

  // --- Query para buscar os detalhes do Cliente ---
  const { data: customerData } = useQueryWithLoader({
    queryKey: ['customerDetails', id],
    queryFn: async () => {
      showLoader()
      const response = await baseServiceGet(entities.customers.getById(id))
      return {
        ...response.result,
        cpf_cnpj: maskCpfCnpj(response.result.cpf_cnpj),
        parent_id: response.result.parent?.name ?? '-',
      }
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  })

  const {
    filters: customerSubclientFilters,
    setFilters: applyCustomerSubclientFilters,
    pagination: customerSubclientPagination,
    changePage: changeCustomerSubclientPage,
    setPagination: setCustomerSubclientPagination,
  } = useUrlFilters(
    {
      parent_id: id,
      deleted_at: false,
    },
    'customerSubclients'
  )

  const {
    handleFilterApply: handleCustomerSubclientFilterApply,
    handleFilterClear: handleCustomerSubclientFilterClear,
  } = useFilterHandlers(applyCustomerSubclientFilters)

  // Gerenciamento de filtros locais (para o FilterForm)
  const { localFilterValues: localCustomerSubclientFilterValues } =
    useGenericFilter(
      {
        customer_id: id,
        deleted_at: false,
      },
      handleCustomerSubclientFilterApply,
      handleCustomerSubclientFilterClear
    )

  const {
    data: customerSubclientsData,
    isLoading: isLoadingCustomerSubclients,
    isFetching: isFetchingCustomerSubclients,
  } = useQueryWithLoader({
    queryKey: ['customerSubclients', id, customerSubclientFilters],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(
        customerSubclientFilters
      )

      const params = {
        filters: filtersTransformed,
        page: customerSubclientPagination.page,
        perPage: customerSubclientPagination.perPage,
        sort: { column: 'created_at', order: 'desc' },
      }
      const response = await baseServiceGet(entities.customers.get, params)

      return response.result
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    select: (response) => ({
      customerSubclients: response.data?.map((item) => ({
        id: item.id,
        alias: item.alias,
        name: item.name,
        email: item.email ?? '-',
        cpf_cnpj: item.cpf_cnpj ? maskCpfCnpj(item.cpf_cnpj) : '-',
        phone: item.phone ? maskPhone(item.phone) : '-',
        isDeleted: !!item.deleted_at,
      })),
      totalPages: response.last_page,
    }),
  })

  const { customerSubclients = [], totalPagesCustomerSubclients = 1 } =
    customerSubclientsData || {}

  const columnCustomerSubclients = [
    { key: 'alias', label: 'Apelido' },
    { key: 'name', label: 'Nome' },
    { key: 'cpf_cnpj', label: 'CPF/CNPJ' },
    { key: 'phone', label: 'Telefone' },
    { key: 'email', label: 'E-mail' },
  ]

  const customerSubclientsActions = useMemo(
    () => [
      {
        id: 'view',
        icon: faEye,
        title: 'Ver Detalhes',
        buttonClass: 'btn-primary',
        permission: 'Atualizar destinos de ordens de serviço',
        onClick: (customerSubclient) =>
          navigate(`/aluguel/clientes/detalhes/${customerSubclient.id}`),
        show: (customerSubclient) =>
          !customerSubclient.isDeleted &&
          canAccess('Atualizar destinos de ordens de serviço'),
      },
    ],
    [canAccess, navigate]
  )

  const {
    filters: addressFilters,
    setFilters: applyAddressFilters,
    pagination: addressPagination,
    changePage: changeAddressPage,
    setPagination: setAddressPagination,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    'address'
  )

  const {
    handleFilterApply: handleAddressFilterApply,
    handleFilterClear: handleAddressFilterClear,
  } = useFilterHandlers(applyAddressFilters)

  // Gerenciamento de filtros locais (para o FilterForm)
  const { localFilterValues: localAddressFilterValues } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleAddressFilterApply,
    handleAddressFilterClear
  )

  const {
    data: addressesData,
    isLoading: isLoadingAddresses,
    isFetching: isFetchingAddresses,
  } = useQueryWithLoader({
    queryKey: ['customerAddresses', id, addressFilters, addressPagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(addressFilters)
      const params = {
        filters: filtersTransformed,
        page: addressPagination.page,
        perPage: addressPagination.perPage,
      }
      const response = await baseServiceGet(
        entities.customers.addresses.get(id),
        params
      )
      return response.result
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    select: (data) => ({
      addresses: data.data.map((address) => ({
        id: address.id,
        alias: address.alias,
        zip: safe(address.zip ? maskCep(address.zip) : '-'),
        street: safe(address.street),
        district: safe(address.district),
        contact_name: safe(address.contact_name),
        contact_phone: safe(address.contact_phone),
        contact_email: safe(address.contact_email),
        inRequest: !!address.in_request,
        inTransaction: !!address.in_transaction,
        isDeleted: !!address.deleted_at,
      })),
      totalPages: data.last_page,
    }),
  })

  const columnAddress = [
    { key: 'alias', label: 'Apelido' },
    { key: 'street', label: 'Rua' },
    { key: 'district', label: 'Bairro' },
    { key: 'zip', label: 'CEP' },
    { key: 'contact_name', label: 'Contato' },
    { key: 'contact_phone', label: 'Telefone' },
    { key: 'contact_email', label: 'Email do Contato' },
  ]

  const { addresses = [], totalPagesAddress = 1 } = addressesData || {}

  const refreshAddresses = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['customerAddresses', id] })
  }, [queryClient, id])

  const {
    filters: contactFilters,
    setFilters: applyContactFilters,
    pagination: contactPagination,
    changePage: changeContactPage,
    setPagination: setContactPagination,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    'contact'
  )

  const {
    handleFilterApply: handleContactFilterApply,
    handleFilterClear: handleContactFilterClear,
  } = useFilterHandlers(applyContactFilters)

  // Gerenciamento de filtros locais (para o FilterForm)
  const { localFilterValues: localContactFilterValues } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleContactFilterApply,
    handleContactFilterClear
  )

  const {
    data: contactsData,
    isLoading: isLoadingContacts,
    isFetching: isFetchingContacts,
  } = useQueryWithLoader({
    queryKey: ['customerContacts', id, contactFilters, contactPagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(contactFilters)
      const params = {
        filters: filtersTransformed,
        page: contactPagination.page,
        perPage: contactPagination.perPage,
      }
      const response = await baseServiceGet(
        entities.customers.contacts.get(id),
        params
      )
      return response.result
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    select: (data) => ({
      contacts: data.data.map((contact) => ({
        id: contact.id,
        name: contact.name,
        number: contact.phone,
        isDeleted: !!contact.deleted_at,
      })),
      totalPages: data.last_page,
    }),
  })

  const columnContact = [
    { key: 'name', label: 'Nome' },
    { key: 'number', label: 'Número' },
  ]

  const { contacts = [], totalPages: totalPagesContact = 1 } =
    contactsData || {}

  const refreshContacts = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['customerContacts', id] })
  }, [queryClient, id])

  const inactiveAddressMutation = useMutation({
    mutationFn: async (addressId) => {
      showLoader()
      const response = await baseServiceDelete(
        entities.customers.addresses.delete(id, addressId)
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) refreshAddresses()
    },
    onError: (err) => {
      console.error('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  const activeAddressMutation = inactiveAddressMutation

  const inactiveContactMutation = useMutation({
    mutationFn: async (contactId) => {
      showLoader()
      const response = await baseServiceDelete(
        entities.customers.contacts.delete(id, contactId)
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) refreshContacts()
    },
    onError: (err) => {
      console.error('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  const activeContactMutation = inactiveContactMutation

  const addressActions = useMemo(
    () => [
      {
        id: 'viewLocations',
        icon: faMapMarkerAlt,
        title: 'Localizações',
        buttonClass: 'btn-info',
        permission: 'Listar endereços de clientes',
        onClick: (address) =>
          navigate(
            `/aluguel/clientes/${id}/endereco/${address.id}/localizacoes`
          ),
        show: (address) => canAccess('Listar endereços de clientes'),
      },
      {
        id: 'viewDetails',
        icon: faEye,
        title: 'Ver detalhes',
        buttonClass: 'btn-info',
        permission: 'Listar endereços de clientes',
        onClick: (address) =>
          navigate(`/aluguel/clientes/${id}/endereco/${address.id}/detalhes`),
        show: (address) => canAccess('Listar endereços de clientes'),
      },
      {
        id: 'edit',
        icon: faEdit,
        title: 'Editar',
        buttonClass: 'btn-primary',
        permission: 'Atualizar endereços de clientes',
        onClick: (address) =>
          navigate(`/aluguel/clientes/${id}/endereco/${address.id}/editar`),
        show: (address) =>
          !address.isDeleted &&
          (!address.inRequest || !address.inTransaction) &&
          canAccess('Atualizar endereços de clientes'),
      },
      {
        id: 'delete',
        icon: faTrash,
        title: 'Excluir',
        buttonClass: 'btn-danger',
        permission: 'Excluir endereços de clientes',

        onClick: (address) =>
          showConfirmation(
            address,
            'Você tem certeza que deseja excluir?',
            async () => {
              try {
                await inactiveAddressMutation.mutateAsync(address.id)
              } catch (error) {
                console.error('Erro ao excluir endereço:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (address) =>
          !address.isDeleted &&
          (!address.inRequest || !address.inTransaction) &&
          canAccess('Excluir endereços de clientes'),
      },
      {
        id: 'activate',
        icon: faUndo,
        title: 'Restaurar',
        buttonClass: 'btn-info',
        permission: 'Excluir endereços de clientes',
        onClick: (address) =>
          showConfirmation(
            address,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activeAddressMutation.mutateAsync(address.id)
              } catch (error) {
                console.error('Erro ao restaurar endereço:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (address) =>
          address.isDeleted && canAccess('Excluir endereços de clientes'),
      },
    ],
    [
      navigate,
      id,
      canAccess,
      showConfirmation,
      inactiveAddressMutation,
      activeAddressMutation,
    ]
  )

  const contactActions = useMemo(
    () => [
      {
        id: 'edit',
        icon: faEdit,
        title: 'Editar',
        buttonClass: 'btn-primary',
        permission: 'Atualizar contato do cliente',
        onClick: (contact) =>
          navigate(`/aluguel/clientes/${id}/contato/editar/${contact.id}`),
        show: (contact) => canAccess('Atualizar contatos de clientes'),
      },
      {
        id: 'delete',
        icon: faTrash,
        title: 'Excluir',
        buttonClass: 'btn-danger',
        permission: 'Excluir contato do cliente',
        onClick: (contact) =>
          showConfirmation(
            contact,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactiveContactMutation.mutateAsync(contact.id)
              } catch (error) {
                console.error('Erro ao excluir contato:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (contact) =>
          !contact.isDeleted && canAccess('Excluir contatos de clientes'),
      },
      {
        id: 'activate',
        icon: faUndo,
        title: 'Restaurar',
        buttonClass: 'btn-info',
        permission: 'Atualizar contato do cliente',
        onClick: (contact) =>
          showConfirmation(
            contact,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activeContactMutation.mutateAsync(contact.id)
              } catch (error) {
                console.error('Erro ao restaurar contato:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (contact) =>
          contact.isDeleted && canAccess('Atualizar contatos de clientes'),
      },
    ],
    [
      navigate,
      id,
      canAccess,
      showConfirmation,
      inactiveContactMutation,
      activeContactMutation,
    ]
  )

  // --- Efeito para exibir notificações da rota (se houver) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Detalhes do Cliente"
        showBackButton={true}
        backUrl="/aluguel/clientes/"
      />
      <div className="container-fluid p-1">
        {/* Renderiza os detalhes do cliente se os dados estiverem disponíveis */}
        {customerData ? (
          <DetailsSectionRenderer
            sections={detailsCustomerFields}
            formData={customerData}
            showSection={false}
            readOnly
          />
        ) : (
          <div className="text-center py-4 text-muted">
            Não foi possível carregar os detalhes do cliente.
          </div>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <ListHeader
            title="Subclientes"
            buttonText="Adicionar Subcliente"
            buttonLink={`/aluguel/clientes/criar?parent_id=${id}`}
            canAccess={canAccess}
            permission="Criar clientes"
          />

          <TableWithInactiveFilter
            data={customerSubclients}
            columns={columnCustomerSubclients}
            actions={customerSubclientsActions}
            totalPages={totalPagesCustomerSubclients}
            currentPage={customerSubclientPagination.page - 1}
            onPageChange={changeCustomerSubclientPage}
            rowsPerPage={customerSubclientPagination.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value
              setCustomerSubclientPagination({ page: 1, perPage: newPerPage })
            }}
            filterValues={localCustomerSubclientFilterValues}
            localFilterValues={localCustomerSubclientFilterValues}
            applyUrlFilters={applyCustomerSubclientFilters}
            type="default"
            isLoading={isLoadingCustomerSubclients}
            isFetching={isFetchingCustomerSubclients}
          />
        </Box>

        <ListHeader
          title="Destinos"
          buttonText="Adicionar Destinos"
          buttonLink={`/aluguel/clientes/${id}/endereco/adicionar`}
          canAccess={canAccess}
          permission="Criar endereços de clientes"
        />

        <TableWithInactiveFilter
          data={addresses}
          columns={columnAddress}
          actions={addressActions}
          totalPages={totalPagesAddress}
          currentPage={addressPagination.page - 1}
          onPageChange={changeAddressPage}
          rowsPerPage={addressPagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setAddressPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localAddressFilterValues}
          localFilterValues={localAddressFilterValues}
          applyUrlFilters={applyAddressFilters}
          type="default"
          isLoading={isLoadingAddresses}
          isFetching={isFetchingAddresses}
        />

        <ListHeader
          title="Contatos"
          buttonText="Adicionar Contato"
          buttonLink={`/aluguel/clientes/${id}/contato/adicionar`}
          canAccess={canAccess}
          permission="Criar contatos de clientes"
        />

        <TableWithInactiveFilter
          data={contacts}
          columns={columnContact}
          actions={contactActions}
          totalPages={totalPagesContact}
          currentPage={contactPagination.page - 1}
          onPageChange={changeContactPage}
          rowsPerPage={contactPagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setContactPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localContactFilterValues}
          localFilterValues={localContactFilterValues}
          applyUrlFilters={applyContactFilters}
          type="default"
          isLoading={isLoadingContacts}
          isFetching={isFetchingContacts}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={
            selectedItem
              ? selectedItem.street
                ? `${selectedItem.street} - ${selectedItem.zip}`
                : selectedItem.name
              : ''
          }
          text={action.text}
        />
      </div>
    </MainLayout>
  )
}

export default CustomerDetailsPage